import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Slide from "@mui/material/Slide";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VideocamIcon from "@mui/icons-material/Videocam";
import EventIcon from "@mui/icons-material/Event";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SchoolIcon from "@mui/icons-material/School";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EmailIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";

import { useUpcomingDemos } from "../../context/UpcomingDemosContext";

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const STATUS_LABELS = {
  new_request: "New",
  contacted: "Contacted",
  scheduled: "Scheduled",
};

const STATUS_COLORS = {
  new_request: "info",
  contacted: "primary",
  scheduled: "secondary",
};

function isoToFlag(iso) {
  if (!iso || typeof iso !== "string") return "";
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const base = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + code.charCodeAt(0) - a,
    base + code.charCodeAt(1) - a
  );
}

function initials({ first_name, last_name, full_name, email }) {
  const fn = (first_name || "").trim();
  const ln = (last_name || "").trim();
  if (fn || ln) return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase() || "?";
  const fall = (full_name || email || "?").trim();
  return fall.slice(0, 2).toUpperCase();
}

/*
  next_slot.start_iso is a floating wall-clock string (no Z). We construct
  the local Date from its components so the modal renders the same time
  the professor will see when they open the email/calendar invite.
*/
function parseFloatingIso(iso) {
  if (!iso || typeof iso !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(iso);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match.map(Number);
  return new Date(y, mo - 1, d, h, mi, s);
}

function formatSlotDate(slot) {
  if (!slot) return "—";
  const d = parseFloatingIso(slot.start_iso);
  if (!d || Number.isNaN(d.getTime())) return slot.date || "—";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatSlotTimeRange(slot) {
  if (!slot) return "";
  const start = parseFloatingIso(slot.start_iso);
  const end = parseFloatingIso(slot.end_iso);
  if (!start || !end) return `${slot.start_time || ""}–${slot.end_time || ""}`;
  const fmt = (date) =>
    date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${fmt(start)} – ${fmt(end)}`;
}

function describeRelative(slot) {
  if (!slot) return "";
  const target = parseFloatingIso(slot.start_iso);
  if (!target) return "";
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < -60) return "In progress / overdue";
  if (diffMin < 0) return "Starting now";
  if (diffMin < 60) return `In ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `In ${diffH}h`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return "Tomorrow";
  if (diffD < 7) return `In ${diffD} days`;
  return target.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function isToday(slot) {
  if (!slot) return false;
  const d = parseFloatingIso(slot.start_iso);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function MetaRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: "text.secondary", mt: "2px" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

function CopyChip({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      /* clipboard blocked — nothing we can do, ignore */
    }
  };
  return (
    <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
      <Chip
        size="small"
        variant="outlined"
        icon={copied ? <CheckIcon /> : <ContentCopyIcon />}
        label={value}
        onClick={onCopy}
        sx={{
          maxWidth: "100%",
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 240,
          },
        }}
      />
    </Tooltip>
  );
}

function DemoCard({ item, onClose }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showOthers, setShowOthers] = useState(false);
  const status = item.status || "new_request";
  const isHappeningToday = isToday(item.next_slot);
  const flag = isoToFlag(item.professor?.country);
  const professorName = item.professor?.full_name || item.professor?.email || "Professor";
  const relative = describeRelative(item.next_slot);

  const openMeet = (e) => {
    e.stopPropagation();
    if (!item.meet_link) return;
    window.open(item.meet_link, "_blank", "noopener,noreferrer");
  };

  const openGCal = (e) => {
    e.stopPropagation();
    if (!item.gcal_url) return;
    window.open(item.gcal_url, "_blank", "noopener,noreferrer");
  };

  const goToCrm = () => {
    onClose?.();
    navigate("/demo-requests");
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: isHappeningToday ? "primary.main" : "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        position: "relative",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: isHappeningToday
          ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}`
          : `0 1px 4px ${alpha(theme.palette.common.black, 0.04)}`,
      }}
    >
      {/* Accent bar */}
      <Box
        sx={{
          height: 4,
          background: isHappeningToday
            ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            : `linear-gradient(90deg, ${alpha(
                theme.palette.primary.main,
                0.4
              )}, ${alpha(theme.palette.secondary.main, 0.4)})`,
        }}
      />

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Header: avatar + name + status */}
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            src={item.professor?.profile_image || undefined}
            sx={{
              width: 44,
              height: 44,
              bgcolor: "primary.main",
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            {initials(item.professor || {})}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              {flag ? (
                <Typography
                  component="span"
                  sx={{ fontSize: "1.3rem", lineHeight: 1, flexShrink: 0 }}
                  aria-label={item.professor?.country || ""}
                >
                  {flag}
                </Typography>
              ) : null}
              <Typography
                variant="subtitle1"
                fontWeight={700}
                noWrap
                title={professorName}
                sx={{ minWidth: 0, fontSize: "1.05rem", lineHeight: 1.25 }}
              >
                {professorName}
              </Typography>
            </Stack>
            {item.university?.name ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={item.university.name}
              >
                {item.university.name}
              </Typography>
            ) : null}
          </Box>
          <Chip
            size="small"
            label={STATUS_LABELS[status] || status}
            color={STATUS_COLORS[status] || "default"}
            sx={{
              fontWeight: 700,
              /* Force a filled background per status so the chip stays
                 readable in light mode even if the template's default
                 Chip styles render it transparent. */
              bgcolor: theme.palette[STATUS_COLORS[status] || "primary"]?.main,
              color: theme.palette.getContrastText(
                theme.palette[STATUS_COLORS[status] || "primary"]?.main ||
                  theme.palette.primary.main
              ),
              "& .MuiChip-label": {
                color: theme.palette.getContrastText(
                  theme.palette[STATUS_COLORS[status] || "primary"]?.main ||
                    theme.palette.primary.main
                ),
              },
            }}
          />
        </Stack>

        {/* Big date/time block */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2.5,
            /* IMPORTANT: don't pipe `theme.palette.action.hover` through
               MUI's alpha() helper — it REPLACES the existing alpha, so
               rgba(0,0,0,0.04) becomes rgba(0,0,0,0.8) and the block goes
               almost black in light mode. Mix a soft tint manually instead. */
            background: isHappeningToday
              ? `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.12
                )}, ${alpha(theme.palette.secondary.main, 0.08)})`
              : theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.04)
              : alpha(theme.palette.common.black, 0.035),
            border: "1px solid",
            borderColor: isHappeningToday
              ? alpha(theme.palette.primary.main, 0.3)
              : theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.08)
              : alpha(theme.palette.common.black, 0.08),
            color: "text.primary",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <EventAvailableIcon
                  sx={{
                    color: isHappeningToday ? "primary.main" : "text.secondary",
                    fontSize: 20,
                  }}
                />
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ fontSize: "1rem", color: "text.primary" }}
                >
                  {formatSlotDate(item.next_slot)}
                </Typography>
              </Stack>
              <Typography
                variant="h6"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  fontVariantNumeric: "tabular-nums",
                  color: "text.primary",
                }}
              >
                {formatSlotTimeRange(item.next_slot)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Your local time
              </Typography>
            </Box>
            {relative ? (
              <Chip
                size="small"
                label={relative}
                color={isHappeningToday ? "primary" : "default"}
                variant={isHappeningToday ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  ...(isHappeningToday
                    ? {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.getContrastText(
                          theme.palette.primary.main
                        ),
                        "& .MuiChip-label": {
                          color: theme.palette.getContrastText(
                            theme.palette.primary.main
                          ),
                        },
                      }
                    : {
                        /* Outlined "default" chip in light mode often loses
                           its border on this template; lock contrast in. */
                        color: "text.primary",
                        borderColor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.25)
                            : alpha(theme.palette.common.black, 0.25),
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.common.white, 0.04)
                            : "#fff",
                      }),
                }}
              />
            ) : null}
          </Stack>

          {item.other_slots && item.other_slots.length > 0 ? (
            <>
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowOthers((v) => !v);
                  }}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transition: "transform 0.2s",
                        transform: showOthers ? "rotate(180deg)" : "rotate(0)",
                      }}
                    />
                  }
                  sx={{ textTransform: "none", fontWeight: 600, px: 0, minWidth: 0 }}
                >
                  {item.other_slots.length} other proposed slot
                  {item.other_slots.length > 1 ? "s" : ""}
                </Button>
              </Box>
              <Collapse in={showOthers} unmountOnExit>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {item.other_slots.map((slot, idx) => (
                    <Stack
                      key={`${slot.start_iso}-${idx}`}
                      direction="row"
                      spacing={1}
                      alignItems="baseline"
                      sx={{ pl: 0.5 }}
                    >
                      <EventIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatSlotDate(slot)} · {formatSlotTimeRange(slot)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Collapse>
            </>
          ) : null}
        </Box>

        {/* Action buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<VideocamIcon />}
            onClick={openMeet}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 700,
              py: 1.25,
              borderRadius: 2,
              /* The Material Dashboard template overrides MuiButton-contained
                 and strips the background color — so `color="success"` alone
                 renders as a white pill. Lock in explicit emerald colors
                 with `!important` so the template can't reset them. */
              backgroundColor: "#10b981 !important",
              backgroundImage:
                "linear-gradient(135deg, #10b981 0%, #059669 100%) !important",
              color: "#fff !important",
              boxShadow: "0 2px 6px rgba(16,185,129,0.25)",
              "& .MuiButton-startIcon, & .MuiButton-startIcon svg": {
                color: "#fff !important",
              },
              "&:hover": {
                backgroundColor: "#059669 !important",
                backgroundImage:
                  "linear-gradient(135deg, #059669 0%, #047857 100%) !important",
                boxShadow: "0 6px 16px rgba(16,185,129,0.35)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#10b981 !important",
                opacity: 0.6,
                color: "#fff !important",
              },
            }}
          >
            Join Google Meet
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<EventIcon />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={openGCal}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              py: 1.25,
              borderRadius: 2,
              /* Explicit colors so the outlined variant reads cleanly on
                 light mode (the template's default outlined+primary is so
                 pale it can look disabled). */
              color: theme.palette.primary.main,
              borderColor: alpha(theme.palette.primary.main, 0.5),
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.08)
                  : "#fff",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
              },
              "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                color: theme.palette.primary.main,
              },
            }}
          >
            Add to Google Calendar
          </Button>
        </Stack>

        {/* Meta + copyables */}
        <Stack spacing={1.25} sx={{ mt: 2 }}>
          <MetaRow
            icon={<SchoolIcon sx={{ fontSize: 16 }} />}
            label="Academic role"
            value={item.academic_role}
          />
          <MetaRow
            icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
            label="Teaching area"
            value={item.teaching_area}
          />
          {item.class_size ? (
            <MetaRow
              icon={<GroupsIcon sx={{ fontSize: 16 }} />}
              label="Class size"
              value={item.class_size}
            />
          ) : null}
          {item.main_interest ? (
            <MetaRow
              icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
              label="Main interest"
              value={item.main_interest}
            />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {item.professor?.email ? (
            <CopyChip value={item.professor.email} label="email" />
          ) : null}
          {item.professor?.work_email &&
          item.professor.work_email !== item.professor.email ? (
            <CopyChip value={item.professor.work_email} label="work email" />
          ) : null}
          {item.professor?.phone ? (
            <CopyChip value={item.professor.phone} label="phone" />
          ) : null}
          {item.meet_link ? (
            <CopyChip value={item.meet_link} label="meet link" />
          ) : null}
        </Stack>

        <Divider sx={{ mt: 2, mb: 1.5 }} />
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EmailIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary">
              ID {String(item.id).slice(0, 8)}…
            </Typography>
          </Stack>
          <Button
            size="small"
            onClick={goToCrm}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Open in CRM →
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function EmptyState({ onRefresh }) {
  return (
    <Box
      sx={{
        textAlign: "center",
        py: 6,
        px: 3,
      }}
    >
      <EventAvailableIcon
        sx={{ fontSize: 56, color: "text.disabled", mb: 1.5 }}
      />
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        No upcoming demos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When a professor books a slot, it will appear here with a Join Meet
        button and a one-tap Add to Calendar action.
      </Typography>
      <Button
        size="small"
        startIcon={<RefreshIcon />}
        onClick={onRefresh}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Refresh
      </Button>
    </Box>
  );
}

export default function UpcomingDemosModal() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { isOpen, closeModal, items, total, todayCount, loading, refresh } =
    useUpcomingDemos();

  const todayItems = useMemo(
    () => items.filter((it) => isToday(it.next_slot)),
    [items]
  );
  const laterItems = useMemo(
    () => items.filter((it) => !isToday(it.next_slot)),
    [items]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={closeModal}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      scroll="paper"
      TransitionComponent={fullScreen ? SlideUp : undefined}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          maxHeight: fullScreen ? "100vh" : "92vh",
        },
      }}
    >
      {/* Sticky gradient header — looks great on mobile + desktop */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          px: { xs: 2, sm: 3 },
          py: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "#fff",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <EventAvailableIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Upcoming demos
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {total > 0
                ? `${total} scheduled${
                    todayCount > 0 ? ` · ${todayCount} today` : ""
                  }`
                : "No demos in the queue"}
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={refresh}
              sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={closeModal}
            sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          /* Soft surface for the scroll area. We mix our own tint rather
             than reusing palette.action.hover (which already carries an
             alpha that MUI's alpha() helper would clobber). */
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.025)
              : alpha(theme.palette.common.black, 0.02),
        }}
      >
        {loading && items.length === 0 ? (
          <Stack spacing={2}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={220}
                sx={{ borderRadius: 3 }}
              />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <EmptyState onRefresh={refresh} />
        ) : (
          <Stack spacing={2.5}>
            {todayItems.length > 0 ? (
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.25, px: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      animation: "pulseDot 1.4s ease-in-out infinite",
                      "@keyframes pulseDot": {
                        "0%, 100%": { opacity: 1, transform: "scale(1)" },
                        "50%": { opacity: 0.5, transform: "scale(1.4)" },
                      },
                    }}
                  />
                  <Typography
                    variant="overline"
                    color="primary"
                    sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
                  >
                    Today
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  {todayItems.map((item) => (
                    <DemoCard key={item.id} item={item} onClose={closeModal} />
                  ))}
                </Stack>
              </Box>
            ) : null}

            {laterItems.length > 0 ? (
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    px: 0.5,
                    mb: 1.25,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                  }}
                >
                  Coming up
                </Typography>
                <Stack spacing={2}>
                  {laterItems.map((item) => (
                    <DemoCard key={item.id} item={item} onClose={closeModal} />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
